import React, { useCallback, useEffect, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { LineButton, Cue, originOf } from "../forecast/Lines";
import { DrillScope } from "../forecast/ForecastLayout";
import { OriginBadge } from "../../lib/origin.jsx";
import { useRole } from "../../lib/role.jsx";
import { getSummary, WC_ROLES, EDIT_ROLES } from "../../lib/workcapApi";
import { TargetsPanel } from "./Targets";
import { cn } from "../../lib/utils";

const TONE = { success: "text-success", warning: "text-warning", critical: "text-critical", info: "" };
const STROKE = { success: "rgb(var(--success))", warning: "rgb(var(--warning))", critical: "rgb(var(--critical))", info: "rgb(var(--primary))" };

function Spark({ values, tone }) {
  const v = values.filter(x => x != null);
  if (v.length < 2) return null;
  const lo = Math.min(...v), hi = Math.max(...v), w = 120, h = 28;
  const pts = v.map((x, i) => `${(i / (v.length - 1)) * w},${h - ((x - lo) / (hi - lo || 1)) * (h - 4) - 2}`).join(" ");
  return <svg width={w} height={h} className="shrink-0" aria-hidden><polyline points={pts} fill="none" stroke={STROKE[tone] || STROKE.info} strokeWidth="1.5" /></svg>;
}

function Tile({ k }) {
  const id = k.id.split(".").pop();
  const on = k.tone === "success";
  return (
    <LineButton line={k} className="px-4 py-3.5 bg-surface h-full">
      <div data-testid={`wc-tile-${id}`}>
        <div className="kpi-label flex items-center gap-2"><span className="truncate">{k.label}</span><OriginBadge {...originOf(k)} className="ml-auto" testid={`wc-origin-${id}`} /></div>
        <div className="text-[10.5px] text-muted mt-0.5">{k.lower_better ? "Lower is better" : "Higher is better"}</div>
        <div className="flex items-end justify-between gap-3 mt-2">
          <div><div className={cn("kpi-value text-3xl", TONE[k.tone])} data-testid={`wc-value-${id}`}>{k.display}</div>
            <div className="text-[11px] mt-1"><span className="text-muted">Target {id === "gmroi" ? k.target.toFixed(2) : `${k.target} d`} · </span>
              <span className={TONE[k.tone]} data-testid={`wc-status-${id}`}>{on ? "On target" : "Off target"}</span></div></div>
          <div className="text-right"><Spark values={k.trend} tone={k.tone} /><div className="text-[9.5px] text-muted">12 months</div></div>
        </div>
        <div className="flex justify-end mt-1"><Cue /></div>
      </div>
    </LineButton>
  );
}

// Command Center: DIO, CCC and GMROI for the CEO, CFO and COO, each opening the forecast drill-down.
export default function WorkingCapitalStrip() {
  const { role } = useRole();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => getSummary().then(setD).catch(e => setErr(e.message)), []);
  useEffect(() => { if (WC_ROLES.includes(role?.id)) load(); }, [role?.id, load]);
  if (!WC_ROLES.includes(role?.id)) return null;
  return (
    <DrillScope>
      <div data-testid="wc-strip">
        <div className="flex items-center justify-between mb-2"><div className="kpi-label">Working capital</div>
          {EDIT_ROLES.includes(role?.id) && <button data-testid="wc-edit-targets" onClick={() => setEdit(true)} className="inline-flex items-center gap-1 text-[11.5px] text-primary hover:underline"><SlidersHorizontal size={12} />Edit targets</button>}</div>
        {!d ? <div className="tile p-4 text-sm text-muted" data-testid="wc-loading">{err || "Loading working-capital KPIs…"}</div> : (
          <div className="tile grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border overflow-hidden">
            {d.kpis.map(k => <Tile key={k.id} k={k} />)}
          </div>)}
      </div>
      {edit && <TargetsPanel onClose={() => setEdit(false)} onSaved={load} />}
    </DrillScope>
  );
}
